import React, { Fragment, useMemo } from 'react';
import { IImageRepositoryItem } from './types';
import ImageRepositoryListItem from './ImageRepositoryListItem';
import Icon from '../Icon';
import { translate } from '../../Services/translation';
import ImageRepositoryListItemGroupHeader from './ImageRepositoryListItemGroupHeader';
import { bytesToString, groupByArticleNumber } from './utils';
import clsx from 'clsx';

interface ImageRepositoryBasketProps {
  items: IImageRepositoryItem[];
  onItemSelect: (selected: boolean, item: IImageRepositoryItem) => void;
  onClearItems: () => void;
}

const ImageRepositoryBasket = ({
  items,
  onItemSelect,
  onClearItems,
}: ImageRepositoryBasketProps) => {
  const groupedItems = useMemo(() => groupByArticleNumber(items), [items]);
  const formattedTotalSize = useMemo(
    () =>
      items.length > 0
        ? bytesToString(
            items.reduce((prev, item) => prev + item.fileSizeBytes, 0)
          )
        : undefined,
    [items]
  );

  return (
    <div className="sticky top-0">
      <div>
        <div className="flex justify-between rounded-t bg-gray-800 px-3 py-2 text-white">
          <div className="flex items-center">
            <h6 className="text-md font-normal">
              {translate('image-repository.selected-images')}{' '}
              {formattedTotalSize && <>({formattedTotalSize})</>}
            </h6>
          </div>
          {items.length > 0 && (
            <button
              className="flex items-center gap-2 text-md"
              onClick={onClearItems}
            >
              {translate('general.clear')}
              <Icon name="delete" className="h-6 w-6" />
            </button>
          )}
        </div>
        <div>
          <div className="flex flex-col gap-2 rounded-b border border-t-0 border-solid border-gray-900 bg-white px-3 py-2 text-md">
            {items.length > 0 && (
              <>
                <ImageRepositoryListItemGroupHeader />
                {Object.keys(groupedItems).map((groupKey) => (
                  <Fragment key={`basket-group-${groupKey}`}>
                    {groupedItems[groupKey]?.map((item) => (
                      <ImageRepositoryListItem
                        item={item}
                        onSelect={(selected) =>
                          onItemSelect(selected, item)
                        }
                        selected={true}
                        key={`basket-${item.name}-${item.articleNumber}-list-item`}
                      />
                    ))}
                    <small
                      className={clsx(
                        'mb-2 w-full border-solid border-gray-100 text-right',
                        Object.keys(groupedItems).length > 1 && 'border-b'
                      )}
                    >
                      {groupKey}
                    </small>
                  </Fragment>
                ))}
              </>
            )}
            {items.length == 0 && (
              <h6 className="flex h-20 items-center justify-center text-md">
                {translate('image-repository.no-items-selected')}
              </h6>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageRepositoryBasket;
